import { apiClient } from '../client';
import type { ApiResponse } from '../types';

/**
 * Who is actually using the app.
 *
 * Presence, not sign-ins. Signing in and closing the tab is not testing, and
 * during a test round the question worth answering is whether anybody is
 * opening the thing at all.
 */

export interface AppSessionHeartbeat {
  /** WEB or MOBILE. */
  platform: string;
  /** Stable per browser, so a reconnect extends its own session. */
  deviceKey: string;
  /** Running total of visible seconds. A total, never a delta. */
  activeSeconds: number;
  appVersion?: string | null;
  deviceInfo?: string | null;
}

export interface AppSession {
  appSessionId: number;
  userId: number;
  userName: string | null;
  email: string | null;
  userType: string | null;
  platform: string;
  startedDate: string;
  lastHeartbeatDate: string;
  activeSeconds: number;
  appVersion: string | null;
  deviceInfo: string | null;
}

export interface AppActivityRow {
  userId: number;
  userName: string | null;
  email: string | null;
  userType: string | null;
  sessions: number;
  activeSeconds: number;
  /** Distinct days they showed up. The number that means "testing". */
  activeDays: number;
  firstSeenDate: string;
  lastSeenDate: string;
  usedWeb: boolean;
  usedMobile: boolean;
}

export interface AppActivitySummary {
  days: number;
  since: string;
  people: AppActivityRow[];
  /** Accounts that never appeared. The half worth acting on. */
  absent: AppActivityRow[];
}

export const activityApi = {
  /** Reports presence. The user comes from the token, never from the body. */
  async heartbeat(payload: AppSessionHeartbeat): Promise<number> {
    const { data } = await apiClient.post<ApiResponse<number>>(
      '/api/v1/Activity/heartbeat',
      payload,
    );
    return data.data;
  },

  /** Administrator-only. */
  async getSummary(days = 30): Promise<AppActivitySummary> {
    const { data } = await apiClient.get<ApiResponse<AppActivitySummary>>(
      '/api/v1/Activity/summary',
      { params: { days } },
    );
    return data.data;
  },

  /** Administrator-only. */
  async getSessions(days = 30, userId?: number): Promise<AppSession[]> {
    const { data } = await apiClient.get<ApiResponse<AppSession[]>>(
      '/api/v1/Activity/sessions',
      { params: { days, userId } },
    );
    return data.data ?? [];
  },
};
